import cx from 'classnames'

import { useCallback } from 'react'

import { Media } from '@/components/Media'

import { useSelectCardEvent, useSelectVideoEvent, useViewVideoEvent } from '@/core/analytics/hooks'

import type { CarouselItemSize, MediaCarouselSlide } from '../types'

import { MediaItemStyles } from './styles'

interface Props {
  item: MediaCarouselSlide
  active: number
  size: CarouselItemSize
  isActive: boolean
  totalSlides: number
}

const getMediaItemSelectContentEventProps = (item: MediaCarouselSlide) => {
  const itemId = item.video ? 'premium_web_video_meio_06' : 'premium_web_card_meio_10'
  const eventLabel = item.video
    ? `playlist-central-black-${item.title}`
    : `evitar-golpes-na-black-friday-${item.title}`

  return {
    itemId,
    label: eventLabel,
  }
}

const useSelectEventHook = {
  video: useSelectVideoEvent,
  card: useSelectCardEvent,
}

const MediaItem = ({ item, isActive, active, size, totalSlides }: Props) => {
  const ariaLabel = item.ariaLabel || item.image?.alt || `Item ${active + 1} de ${totalSlides}`

  const mediaType = item.video ? 'video' : 'card'

  const selectContentEventProps = getMediaItemSelectContentEventProps(item)
  const selectContentEvent = useSelectEventHook[mediaType]()
  const viewContentEvent = useViewVideoEvent()

  const width = isActive ? size.activeWidth : size.width
  const height = isActive ? size.activeHeight : size.height

  const handleEvents = useCallback(() => {
    selectContentEvent({ ...selectContentEventProps })
    if (!item.video) return
    return viewContentEvent({ label: item.title })
  }, [selectContentEventProps, selectContentEvent, viewContentEvent, item.title, item.video])

  return (
    <>
      {item.href && (
        <MediaItemStyles
          className={cx({ '--active': isActive })}
          aria-label={ariaLabel}
          href={item.href}
          size={size}
          onClick={handleEvents}
        >
          <Media
            aspectRatio="fixed"
            width={width}
            height={height}
            type={item.type}
            layout="responsive"
            video={item.video}
            image={{
              ...item.image,
              alt: ariaLabel,
            }}
          />
        </MediaItemStyles>
      )}
      {!item.href && (
        <MediaItemStyles
          as="article"
          className={cx({ '--active': isActive })}
          size={size}
          tabIndex={1}
          aria-label={ariaLabel}
          onClick={handleEvents}
        >
          <Media
            aspectRatio="16x9"
            width={width}
            height={height}
            type={item.type}
            video={item.video}
            image={{ ...item.image, alt: ariaLabel }}
          />
        </MediaItemStyles>
      )}
    </>
  )
}

export default MediaItem
